import { FC, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { spacing, colors, fontStyles } from '../../../styles';
import { cleanMarkdownContent } from '../../../utils/contentCleaning';
import { ToolRendererProps } from './types';

const INITIAL_VISIBLE = 3;

const monoNum = css`
  font-family: monospace;
  font-variant-numeric: tabular-nums;
`;

const showMoreStyle = css`
  ${fontStyles.mouse}
  color: ${colors.petrolBlue};
  cursor: pointer;
  padding: ${spacing.milli} 0;

  html[data-theme='dark'] & {
    color: ${colors.skyBlue};
  }
`;

// Render structured earnings sections with collapsible lists
const StructuredEarningsDisplay: FC<{
  summaries: string[];
  key_points: any[];
  key_risks: any[];
  symbol?: string;
}> = ({ summaries, key_points, key_risks, symbol }) => {
  const [pointsExpanded, setPointsExpanded] = useState(false);
  const [risksExpanded, setRisksExpanded] = useState(false);

  const visiblePoints = pointsExpanded ? key_points : key_points.slice(0, INITIAL_VISIBLE);
  const visibleRisks = risksExpanded ? key_risks : key_risks.slice(0, INITIAL_VISIBLE);

  return (
    <div
      css={css`
        color: ${colors.liquorice};
        ${fontStyles.mouse}
        html[data-theme='dark'] & {
          color: ${colors.sugar};
        }
      `}
    >
      {symbol && (
        <div
          css={css`
            ${fontStyles.cat}
            font-weight: 600;
            margin-bottom: ${spacing.centi};
            color: ${colors.petrolBlue};
            html[data-theme='dark'] & {
              color: ${colors.skyBlue};
            }
          `}
        >
          {symbol}
        </div>
      )}
      {/* Summaries Section */}
      {summaries.length > 0 && (
        <div
          css={css`
            margin-bottom: ${spacing.deci};
          `}
        >
          <h3
            css={css`
              ${fontStyles.cat}
              font-weight: 600;
              margin: 0 0 ${spacing.centi} 0;
              color: ${colors.liquorice};
              html[data-theme='dark'] & {
                color: ${colors.sugar};
              }
            `}
          >
            Key Summaries
          </h3>
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: ${spacing.milli};
            `}
          >
            {summaries.slice(0, 3).map((summary: string, index: number) => (
              <div
                key={index}
                css={css`
                  padding: ${spacing.centi};
                  background-color: rgba(0, 0, 0, 0.02);
                  border-radius: 6px;
                  border-left: 3px solid ${colors.petrolBlue};
                  ${fontStyles.mouse}
                  html[data-theme='dark'] & {
                    background-color: rgba(255, 255, 255, 0.03);
                    border-left-color: ${colors.skyBlue};
                  }
                `}
              >
                {summary}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Key Points Section */}
      {key_points.length > 0 && (
        <div
          css={css`
            margin-bottom: ${spacing.deci};
          `}
        >
          <h3
            css={css`
              ${fontStyles.cat}
              font-weight: 600;
              margin: 0 0 ${spacing.centi} 0;
              color: ${colors.liquorice};
              html[data-theme='dark'] & {
                color: ${colors.sugar};
              }
            `}
          >
            Key Points (<span css={monoNum}>{key_points.length}</span>)
          </h3>
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: ${spacing.milli};
            `}
          >
            {visiblePoints.map((point: any, index: number) => (
              <div
                key={index}
                css={css`
                  padding: ${spacing.centi};
                  background-color: rgba(0, 128, 0, 0.05);
                  border-radius: 6px;
                  ${fontStyles.mouse}
                  html[data-theme='dark'] & {
                    background-color: rgba(0, 255, 0, 0.05);
                  }
                `}
              >
                <div>{point.details || point}</div>
                {point.page && (
                  <div
                    css={css`
                      margin-top: ${spacing.milli};
                      ${fontStyles.flea}
                      color: ${colors.silver};
                      font-style: italic;
                    `}
                  >
                    Page <span css={monoNum}>{point.page}</span>
                  </div>
                )}
              </div>
            ))}
            {key_points.length > INITIAL_VISIBLE && !pointsExpanded && (
              <span css={showMoreStyle} onClick={() => setPointsExpanded(true)}>
                Show {key_points.length - INITIAL_VISIBLE} more...
              </span>
            )}
          </div>
        </div>
      )}

      {/* Key Risks Section */}
      {key_risks.length > 0 && (
        <div>
          <h3
            css={css`
              ${fontStyles.cat}
              font-weight: 600;
              margin: 0 0 ${spacing.centi} 0;
              color: ${colors.liquorice};
              html[data-theme='dark'] & {
                color: ${colors.sugar};
              }
            `}
          >
            Key Risks (<span css={monoNum}>{key_risks.length}</span>)
          </h3>
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: ${spacing.milli};
            `}
          >
            {visibleRisks.map((risk: any, index: number) => (
              <div
                key={index}
                css={css`
                  padding: ${spacing.centi};
                  background-color: rgba(255, 0, 0, 0.05);
                  border-radius: 6px;
                  ${fontStyles.mouse}
                  html[data-theme='dark'] & {
                    background-color: rgba(255, 0, 0, 0.05);
                  }
                `}
              >
                <div>{risk.details || risk}</div>
                {risk.page && (
                  <div
                    css={css`
                      margin-top: ${spacing.milli};
                      ${fontStyles.flea}
                      color: ${colors.silver};
                      font-style: italic;
                    `}
                  >
                    Page <span css={monoNum}>{risk.page}</span>
                  </div>
                )}
              </div>
            ))}
            {key_risks.length > INITIAL_VISIBLE && !risksExpanded && (
              <span css={showMoreStyle} onClick={() => setRisksExpanded(true)}>
                Show {key_risks.length - INITIAL_VISIBLE} more...
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Render a single symbol's earnings data
const SingleEarningsDisplay: FC<{ data: any; symbol?: string }> = ({ data, symbol }) => {
  // Handle structured earnings data with summaries/key_points/key_risks
  if (data && typeof data === 'object' && (data.summaries || data.key_points || data.key_risks)) {
    const { summaries = [], key_points = [], key_risks = [] } = data;
    return (
      <StructuredEarningsDisplay summaries={summaries} key_points={key_points} key_risks={key_risks} symbol={symbol} />
    );
  }

  // Handle earnings data from the nested structure (symbol -> earnings -> actual data)
  const earningsData = data?.earnings || data;
  if (earningsData && typeof earningsData === 'object') {
    // Check for various earnings data fields
    const hasEarningsContent =
      earningsData.quarterly_earnings ||
      earningsData.annual_earnings ||
      earningsData.estimates ||
      earningsData.surprises ||
      earningsData.analysis;

    if (hasEarningsContent) {
      const content = earningsData.analysis || JSON.stringify(earningsData, null, 2);
      return (
        <div
          css={css`
            color: ${colors.liquorice};
            ${fontStyles.mouse}
            html[data-theme='dark'] & {
              color: ${colors.sugar};
            }
          `}
        >
          {symbol && (
            <div
              css={css`
                ${fontStyles.cat}
                font-weight: 600;
                margin-bottom: ${spacing.centi};
                color: ${colors.petrolBlue};
                html[data-theme='dark'] & {
                  color: ${colors.skyBlue};
                }
              `}
            >
              {symbol}
            </div>
          )}
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {typeof content === 'string' ? cleanMarkdownContent(content) : JSON.stringify(content)}
          </ReactMarkdown>
        </div>
      );
    }
  }

  // Fallback for error or minimal data
  if (data?.error) {
    return (
      <div
        css={css`
          ${fontStyles.mouse}
          color: ${colors.berry};
        `}
      >
        {symbol && <strong>{symbol}: </strong>}
        {data.error}
      </div>
    );
  }

  return (
    <div
      css={css`
        ${fontStyles.mouse}
        color: ${colors.silver};
      `}
    >
      {symbol && <strong>{symbol}: </strong>}
      Earnings data available
    </div>
  );
};

export const EarningsRenderer: FC<ToolRendererProps> = ({ output, isLoading }) => {
  // Handle both string and object outputs with proper typing - must be before early returns
  const parsedOutput = useMemo((): any => {
    if (typeof output === 'string') {
      try {
        return JSON.parse(output);
      } catch {
        return null;
      }
    }
    return output;
  }, [output]);

  if (isLoading) {
    return (
      <div
        css={css`
          ${fontStyles.mouse}
          color: ${colors.silver};
        `}
      >
        Analyzing earnings...
      </div>
    );
  }

  // Handle batch format: {"earnings_analysis": [...], "metadata": {...}}
  if (parsedOutput && typeof parsedOutput === 'object' && Array.isArray(parsedOutput.earnings_analysis)) {
    const results = parsedOutput.earnings_analysis;
    return (
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: ${spacing.deci};
        `}
      >
        <div
          css={css`
            ${fontStyles.flea}
            color: ${colors.silver};
            margin-bottom: ${spacing.milli};
          `}
        >
          Analyzed <span css={monoNum}>{results.length}</span> symbol{results.length !== 1 ? 's' : ''}
        </div>
        {results.map((item: any, index: number) => (
          <div
            key={item.symbol || index}
            css={css`
              ${index > 0 ? `border-top: 1px solid ${colors.cloud}; padding-top: ${spacing.deci};` : ''}
              html[data-theme='dark'] & {
                border-color: ${colors.tungsten};
              }
            `}
          >
            <SingleEarningsDisplay data={item.earnings || item} symbol={item.symbol} />
          </div>
        ))}
      </div>
    );
  }

  // Check if parsedOutput has structured earnings data (single symbol, legacy format)
  if (
    parsedOutput &&
    typeof parsedOutput === 'object' &&
    (parsedOutput.summaries || parsedOutput.key_points || parsedOutput.key_risks)
  ) {
    const { summaries = [], key_points = [], key_risks = [] } = parsedOutput;
    return <StructuredEarningsDisplay summaries={summaries} key_points={key_points} key_risks={key_risks} />;
  }

  // Extract content from various possible locations for backward compatibility
  // Use parsedOutput for structured data, but fall back to raw output if it's a string (e.g., markdown)
  const dataSource = parsedOutput || output;
  const content =
    typeof output === 'string' && !parsedOutput
      ? output
      : dataSource?.content ||
        dataSource?.analysis ||
        dataSource?.summary ||
        dataSource?.output ||
        (typeof dataSource === 'object' && dataSource && Object.keys(dataSource).length === 1
          ? Object.values(dataSource)[0]
          : '') ||
        '';

  // If we have substantial content with markdown formatting, render it fully
  if (content.length > 100 && (content.includes('## ') || content.includes('### ') || content.includes('|'))) {
    return (
      <div
        css={css`
          color: ${colors.liquorice};
          ${fontStyles.mouse}

          html[data-theme='dark'] & {
            color: ${colors.sugar};
          }

          /* Markdown styles matching ChatPage */
          & p {
            margin: 0 0 ${spacing.centi} 0;
            &:last-child {
              margin-bottom: 0;
            }
          }
          & h1 {
            ${fontStyles.lion}
            margin: 0 0 ${spacing.centi} 0;
          }
          & h2 {
            ${fontStyles.dog}
            margin: 0 0 ${spacing.centi} 0;
          }
          & h3 {
            ${fontStyles.cat}
            font-weight: 600;
            margin: 0 0 ${spacing.milli} 0;
          }
          & ul,
          & ol {
            margin: ${spacing.centi} 0;
            padding-left: ${spacing.regular};
          }
          & li {
            margin: ${spacing.milli} 0;
          }
          & code {
            background-color: rgba(0, 0, 0, 0.1);
            padding: ${spacing.micro} ${spacing.milli};
            border-radius: 4px;
          }
          & pre {
            background-color: rgba(0, 0, 0, 0.1);
            padding: ${spacing.milli};
            border-radius: 4px;
            overflow-x: auto;
            margin: ${spacing.milli} 0;
          }
          & strong {
            font-weight: normal;
          }
          & em {
            font-style: normal;
          }
          & a {
            color: ${colors.petrolBlue};
            html[data-theme='dark'] & {
              color: ${colors.skyBlue};
            }
          }

          /* Table styles matching ChatPage */
          & .table-wrapper {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            margin: ${spacing.deci} 0;
            border-radius: 8px;
            border: 1px solid ${colors.cloud};
          }

          & table {
            border-collapse: collapse;
            width: 100%;
            display: table;
            font-size: 0.9em;
            margin: 0;
          }

          & th,
          & td {
            border: none;
            border-bottom: 1px solid ${colors.cloud};
            padding: ${spacing.deci};
            text-align: left;
            vertical-align: top;
            white-space: normal;
            word-break: normal;
            line-height: 1.5;
          }

          & th {
            background-color: rgba(0, 0, 0, 0.05);
            font-weight: 600;
            min-width: 120px;
            white-space: nowrap;
            border-bottom: 2px solid ${colors.cloud};
          }

          & td {
            white-space: pre-line;

            & br {
              display: block;
              content: '';
              margin-top: 0.5em;
            }

            & ul,
            & ol {
              margin: 0.5em 0;
              padding-left: 1.5em;
            }

            & li {
              margin: 0.25em 0;
            }

            & strong {
              font-weight: normal;
            }

            & em {
              font-style: normal;
            }
          }

          & tr:nth-of-type(even) {
            background-color: rgba(0, 0, 0, 0.02);
          }

          & tr:last-child td {
            border-bottom: none;
          }

          html[data-theme='dark'] & {
            & .table-wrapper {
              border-color: ${colors.tungsten};
            }

            & th,
            & td {
              border-color: ${colors.tungsten};
            }

            & th {
              background-color: rgba(255, 255, 255, 0.05);
              border-bottom-color: ${colors.tungsten};
            }

            & tr:nth-of-type(even) {
              background-color: rgba(255, 255, 255, 0.02);
            }
          }
        `}
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            table: ({ children, ...props }) => (
              <div className="table-wrapper">
                <table {...props}>{children}</table>
              </div>
            ),
            td: ({ children, ...props }) => <td {...props}>{children}</td>,
            th: ({ children, ...props }) => <th {...props}>{children}</th>,
            strong: ({ children }) => <span>{children}</span>,
            em: ({ children }) => <span>{children}</span>,
          }}
        >
          {cleanMarkdownContent(content)}
        </ReactMarkdown>
      </div>
    );
  }

  // Fallback for short content or simple messages
  return (
    <div
      css={css`
        ${fontStyles.mouse}
        color: ${colors.silver};
      `}
    >
      Earnings analysis completed
    </div>
  );
};
